/**
 * Sezione Fatturazione e Pagamento - Impostazioni per Seller
 * Permette di configurare pagamenti reali, gateway e timing
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Receipt, 
  CreditCard, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Loader2,
  Save,
  Sparkles
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface BillingSettings {
  enabled: boolean;
  payment_timing: 'on_submit' | 'on_confirm';
  preferred_gateway: 'stripe' | 'paypal' | 'both';
  generate_invoices: boolean;
  invoice_prefix: string;
}

const BillingSection = ({ user, t }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [extensionActive, setExtensionActive] = useState(false);
  const [settings, setSettings] = useState<BillingSettings>({
    enabled: false,
    payment_timing: 'on_confirm',
    preferred_gateway: 'both',
    generate_invoices: true,
    invoice_prefix: 'FT'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/billing/settings`);
      setExtensionActive(res.data.extension_active);
      if (res.data.settings) {
        setSettings({
          enabled: res.data.settings.enabled || false,
          payment_timing: res.data.settings.payment_timing || 'on_confirm',
          preferred_gateway: res.data.settings.preferred_gateway || 'both',
          generate_invoices: res.data.settings.generate_invoices ?? true,
          invoice_prefix: res.data.settings.invoice_prefix || 'FT'
        });
      }
    } catch (error) {
      console.error('Error fetching billing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/billing/settings`, settings);
      toast.success('Impostazioni salvate');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Solo per seller
  if (user?.role !== 'seller') return null;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!extensionActive) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Fatturazione e Pagamento
          </CardTitle>
          <CardDescription>
            Ricevi pagamenti reali dai tuoi clienti
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">
              Attiva l'estensione "Fatturazione e Pagamento" dallo Store Estensioni per utilizzare questa funzionalità.
            </p>
            <Button variant="outline" onClick={() => window.location.href = '/extensions'}>
              Vai allo Store Estensioni
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="billing-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Fatturazione e Pagamento
            </CardTitle>
            <CardDescription>
              Configura come ricevere pagamenti dai tuoi clienti
            </CardDescription>
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            <Sparkles className="h-3 w-3 mr-1" />
            0% commissioni primo anno
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Abilitazione pagamenti */}
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">Pagamenti Reali</Label>
            <p className="text-sm text-muted-foreground">
              Abilita per ricevere pagamenti con carta di credito
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            data-testid="billing-enabled-switch"
          />
        </div>

        {settings.enabled && (
          <>
            {/* Momento del pagamento */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Quando far pagare il cliente
              </Label>
              <Select
                value={settings.payment_timing}
                onValueChange={(value: 'on_submit' | 'on_confirm') => 
                  setSettings({ ...settings, payment_timing: value })
                }
              >
                <SelectTrigger data-testid="payment-timing-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_submit">
                    <div className="flex items-center gap-2">
                      <span>All'invio dell'ordine</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="on_confirm">
                    <div className="flex items-center gap-2">
                      <span>Alla conferma dell'ordine</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {settings.payment_timing === 'on_submit' 
                  ? "Il cliente paga subito quando invia l'ordine"
                  : "Il cliente paga quando tu confermi/prendi in carico l'ordine"
                }
              </p>
            </div>

            {/* Gateway preferito */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Metodi di pagamento accettati
              </Label>
              <Select
                value={settings.preferred_gateway}
                onValueChange={(value: 'stripe' | 'paypal' | 'both') => 
                  setSettings({ ...settings, preferred_gateway: value })
                }
              >
                <SelectTrigger data-testid="gateway-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">
                    <span>Stripe + PayPal (consigliato)</span>
                  </SelectItem>
                  <SelectItem value="stripe">
                    <span>Solo Stripe</span>
                  </SelectItem>
                  <SelectItem value="paypal">
                    <span>Solo PayPal</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fatturazione */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Genera Fatture Automatiche</Label>
                  <p className="text-sm text-muted-foreground">
                    Crea PDF fattura per ogni pagamento ricevuto
                  </p>
                </div>
                <Switch
                  checked={settings.generate_invoices}
                  onCheckedChange={(checked) => setSettings({ ...settings, generate_invoices: checked })}
                  data-testid="generate-invoices-switch"
                />
              </div>

              {settings.generate_invoices && (
                <div className="space-y-2">
                  <Label>Prefisso Numero Fattura</Label>
                  <Input
                    value={settings.invoice_prefix}
                    onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value.toUpperCase() })}
                    placeholder="FT"
                    maxLength={5}
                    className="w-32"
                    data-testid="invoice-prefix-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Es. {settings.invoice_prefix}-2024-0001
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Pulsante salva */}
        <div className="pt-4 border-t">
          <Button onClick={handleSave} disabled={saving} data-testid="save-billing-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salva Impostazioni
              </>
            )}
          </Button>
        </div>

        {/* Info credito buyer */}
        {settings.enabled && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  Gestione Credito Buyer
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Puoi impostare soglie di credito per i tuoi clienti fidati dalla pagina 
                  <a href="/relationships" className="underline ml-1">Clienti</a>.
                  Potranno accumulare ordini fino alla soglia prima di pagare.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BillingSection;
